import React, { CSSProperties, ReactNode, RefObject, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './Stack.css';
import { Divider } from './Divider';

interface StackProps {
  wireframe?: boolean;
  style?: CSSProperties;
  maxWidth?: string | number;
  children?: ReactNode;
  className?: string;
  align?: string;
  ref?: RefObject<HTMLDivElement>;
  showOn?: 'compact' | 'medium' | 'expanded' | 'compact medium' | 'medium expanded';
  width?: 'hug' | 'fill';
  height?: 'hug' | 'fill';
  showOverflowBar?: boolean;
  wrap?: boolean;
  group?: boolean;
}

interface HStackProps extends StackProps {
  gridExpanded?: number[];
  gridMedium?: number[];
  gridCompact?: number[];
  sectionSpacing?: boolean;
}

// VStack related props
interface VStackProps extends StackProps {
  scroll?: 'vertical' | 'horizontal' | 'both' | ''
  // ref?: RefObject<HTMLDivElement>;
}


const HStack: React.FC<HStackProps> = ({
  wireframe = false,
  style,
  maxWidth,
  children,
  className,
  align,
  showOn,
  gridExpanded,
  gridMedium,
  gridCompact,
  ref,
  width = 'fill',
  height = 'hug',
  wrap,
  sectionSpacing = false,
  group = false
  // showOverflowBar = true,
}) => {

  const [stylesApplied, setStylesApplied] = useState(false);

  const generateGridTemplateColumns = (grid: number[]) => {
    return `grid-template-columns: ${grid.map(value => `minmax(0, ${value}fr)`).join(' ')};`;
  };

  const generateMediaQuery = (prefix: string, grid: number[], minWidth: number, maxWidth?: number) => {
    const maxQuery = maxWidth ? ` and (max-width: ${maxWidth}px)` : '';
    return `
    @media screen and (min-width: ${minWidth}px)${maxQuery} {
      .h-stack.${prefix}-${grid.join('-')} {
        display: grid;
        gap: ${sectionSpacing ? 'var(--ts-space-v-section) var(--ts-space-h-section)' : '0px'};
        ${generateGridTemplateColumns(grid)}
      }
    }
    .${prefix} .h-stack.${prefix}-${grid.join('-')} {
      display: grid;
      gap: ${sectionSpacing ? 'var(--ts-space-v-section) var(--ts-space-h-section)' : '0px'} !important ;
      ${generateGridTemplateColumns(grid)}
    }
  `;
  };


  const showOnSplit = showOn
    ?.split(" ")
    .map(word =>
      `showOn${word.charAt(0).toUpperCase() + word.slice(1)}`
    )
    .join(" ");


  useEffect(() => {
    if (gridCompact || gridMedium || gridExpanded) {
      const generateStyle = (prefix: string, grid: number[], minWidth: number, maxWidth?: number) => {
        const combinedGrid = grid.join('-');
        const existingStyle = document.querySelector(`style[data-grid="${prefix}-${combinedGrid}"]`);

        // Only create the style if it doesn't already exist
        if (!existingStyle) {
          const css = generateMediaQuery(prefix, grid, minWidth, maxWidth);
          const style = document.createElement('style');
          style.setAttribute('data-grid', `${prefix}-${combinedGrid}`);
          style.appendChild(document.createTextNode(css));
          document.head.appendChild(style);
        }
        return `${prefix}-${combinedGrid}`;
      };

      if (gridCompact) generateStyle('compact', gridCompact, 0, 719);
      if (gridMedium) generateStyle('medium', gridMedium, 720, 1199);
      if (gridExpanded) generateStyle('expanded', gridExpanded, 1200);

      setStylesApplied(true);
    } else {
      setStylesApplied(true); // No grid configuration, render immediately
    }
  }, [gridCompact, gridMedium, gridExpanded]);

  const compactClasses = gridCompact ? ` compact-${gridCompact.join('-')}` : '';
  const mediumClasses = gridMedium ? ` medium-${gridMedium.join('-')}` : '';
  const expandedClasses = gridExpanded ? ` expanded-${gridExpanded.join('-')}` : '';

  const classes = [
    'h-stack',                         // Base class
    className,                         // Optional className prop
    `width-${width}`,                  // Width class
    `height-${height}`,                // Height class
    showOn ? showOnSplit : '',         // Conditional showOnSplit class
    compactClasses,                    // Presumed string of compact classes
    mediumClasses,                     // Presumed string of medium classes
    expandedClasses,                   // Presumed string of expanded classes
    align,                             // Align class (assuming it's a string like 'left')
    wireframe ? 'wireframe' : '',      // Conditional wireframe class
    wrap ? 'wrap' : '',                // Conditional wrap class
    group ? 'group' : '',              // Conditional group class
  ]
    .filter(Boolean)                  // Remove falsy values (e.g., empty strings)
    .join(' ');


  if (!stylesApplied) {
    return null; // Wait for styles to be applied
  }
  return (
    <div
      ref={ref}
      style={{ maxWidth, ...style }}
      className={classes}
    >
      {children}
    </div>
  )
};


const VStack = forwardRef<HTMLDivElement, VStackProps>(
  (
    {
      wireframe = false,
      style,
      maxWidth,
      children,
      className,
      align,
      showOn,
      height = 'hug',
      width = 'fill',
      scroll,
      showOverflowBar = true,
      group = false,
    },
    parentRef // Ref passed from the parent
  ) => {
    const localRef = useRef<HTMLDivElement>(null); // Local ref for internal logic
    const [overflowTop, setOverflowTop] = useState(false);
    const [overflowBottom, setOverflowBottom] = useState(false);

    const showOnSplit = showOn
      ?.split(" ")
      .map(word =>
        `showOn${word.charAt(0).toUpperCase() + word.slice(1)}`
      )
      .join(" ");


    // Expose the local ref to the parent via useImperativeHandle
    useImperativeHandle(parentRef, () => localRef.current);

    useEffect(() => {
      const stack = localRef.current;

      if (scroll === 'vertical' && stack) {
        const handleScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = stack;
          setOverflowTop(scrollTop > 0);
          setOverflowBottom(scrollTop + clientHeight < scrollHeight);
        };

        stack.addEventListener('scroll', handleScroll);

        // Cleanup function
        return () => {
          stack.removeEventListener('scroll', handleScroll);
        };
      }
    }, [scroll]);

    const combinedClasses = [
      'v-stack',
      className,
      `width-${width}`,
      `height-${height}`,
      showOn ? showOnSplit : '',
      align ? align : '',
      wireframe ? 'wireframe' : '',
      scroll ? `scroll-${scroll}` : '',
      overflowTop && showOverflowBar && 'stack-overflow-top-divider',
      overflowBottom && showOverflowBar && 'stack-overflow-bottom-divider',
      group && 'group',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <>
        <div
          ref={localRef} // Attach the local ref for internal usage
          style={{ maxWidth, ...style }}
          className={combinedClasses}
        >
          {children}
        </div>
      </>
    );
  }
);


HStack.displayName = 'HStack'
VStack.displayName = 'VStack'
export { HStack, VStack };
